import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { supabase } from '../lib/supabase'
import { useToast } from '../components/Toast'
import SEO from '../components/SEO'
import Logo from '../components/Logo'
import { trackEvent } from '../lib/posthog'

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

export default function SignUp() {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)
  // Turnstile token, populated by the widget when the user passes the
  // (usually invisible) challenge. We hold it in state rather than just
  // reading from the ref on submit so we can also disable the submit
  // button until the token is present, giving the user a clear signal
  // that the captcha is still verifying.
  const [captchaToken, setCaptchaToken] = useState('')
  const captchaRef = useRef(null)
  const navigate = useNavigate()
  const toast    = useToast()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password.length < 8) { toast('Password must be at least 8 characters', 'error'); return }
    if (password !== confirm) { toast('Passwords do not match', 'error'); return }
    if (!acceptedTerms) { toast('You must accept the Terms & Conditions to create an account', 'error'); return }
    if (!captchaToken) { toast('Please wait a moment for the security check to complete', 'error'); return }

    setLoading(true)
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // After email confirmation, land them on their dashboard. Every
        // new sign-up gets a 30-day trial automatically via the
        // on_auth_user_created trigger, so they have access immediately
        // and don't need to pick a plan first.
        emailRedirectTo: `${window.location.origin}/dashboard`,
        // Cloudflare Turnstile token. Supabase verifies this with
        // Cloudflare server-side before processing the signup. If
        // missing or invalid, signUp returns an error.
        captchaToken,
      },
    })
    setLoading(false)

    if (error) {
      // Turnstile tokens are single-use; once we've sent one with a
      // signUp call (whether it succeeded or failed), we need a fresh
      // one for the next attempt. Reset the widget so it issues a new
      // token, and clear the stored value so the button disables until
      // it arrives.
      captchaRef.current?.reset()
      setCaptchaToken('')
      toast(error.message, 'error')
      return
    }

    // Marketing funnel event. Fires once per successful signup, regardless
    // of whether email confirmation is required (the user has done the
    // conversion action from a marketing POV; whether they confirm their
    // email is a separate downstream metric we don't track yet).
    // No PII in properties -- PostHog gets only the fact that a signup
    // happened. If consent wasn't granted, trackEvent is a no-op.
    trackEvent('signup_completed', {
      email_confirmation_required: !data.session,
    })

    // If session is present, email confirmation is disabled. Send them
    // straight to the dashboard.
    if (data.session) {
      navigate('/dashboard')
      return
    }

    // If no session, email confirmation is required. Show a wait screen.
    setConfirmationSent(true)
  }

  // Confirmation sent screen
  if (confirmationSent) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--surface)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <SEO title="Check your email" noindex />
        <div className="fade-up" style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, background: 'var(--green)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', margin: '0 auto 24px', boxShadow: '0 8px 24px rgba(34,160,107,0.25)' }}>✉️</div>
          <h2 style={{ marginBottom: 12 }}>Check your email</h2>
          <p style={{ color: 'var(--ink-muted)', lineHeight: 1.7, marginBottom: 24 }}>
            We've sent a confirmation link to <strong>{email}</strong>.<br />
            Click the link in the email to activate your account and start your free trial.
          </p>
          <div className="card" style={{ padding: '16px 20px', background: 'var(--accent-light)', border: '1.5px solid var(--accent)', marginBottom: 24 }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--accent)', margin: 0 }}>
              💡 <strong>Tip:</strong> If you don't see it within a minute, check your spam folder.
            </p>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
            Already confirmed?{' '}
            <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 500 }}>Sign in →</Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--surface)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <SEO title="Create your account" noindex />
      <div style={{ position: 'fixed', top: 0, right: 0, width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(72,146,145,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div className="fade-up" style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Logo variant="full" size={80} style={{ margin: '0 auto 20px' }} />
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
            Create your bookkeeper account
          </p>
        </div>

        <div style={{
          background: 'var(--green-light)', border: '1.5px solid var(--green)',
          borderRadius: 'var(--radius)', padding: '12px 18px', marginBottom: 24,
          textAlign: 'center', fontSize: '0.88rem', color: 'var(--green)', fontWeight: 500,
        }}>
          🎉 30-day free trial — no credit card required
        </div>

        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="confirm">Confirm password</label>
              <input
                id="confirm"
                type="password"
                className="input"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Repeat your password"
                required
              />
            </div>

            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              marginTop: 16, cursor: 'pointer',
              fontSize: '0.85rem', color: 'var(--ink-muted)', lineHeight: 1.5,
            }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={e => setAcceptedTerms(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                  Terms &amp; Conditions
                </Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                  Privacy Policy
                </Link>.
              </span>
            </label>

            {/* Cloudflare Turnstile widget. Usually invisible -- it only
                presents a challenge if Cloudflare's risk score for the
                visitor is high enough to warrant one. The widget calls
                onSuccess with a single-use token; we store it and pass
                it to supabase.auth.signUp, which verifies it server-side
                with Cloudflare before processing the signup. The button
                stays disabled until the token arrives, which under
                normal conditions is < 1 second after the form mounts.

                If the site key env var is missing (e.g. local dev with
                no Turnstile setup), we render nothing -- the button will
                stay disabled and the user sees a "security check"
                message, which is the right behaviour: signup will fail
                anyway because Supabase requires a token. */}
            {TURNSTILE_SITE_KEY ? (
              <div style={{ marginTop: 16 }}>
                <Turnstile
                  ref={captchaRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={token => setCaptchaToken(token)}
                  onError={() => setCaptchaToken('')}
                  onExpire={() => setCaptchaToken('')}
                  options={{ theme: 'light', size: 'flexible' }}
                />
              </div>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || !acceptedTerms || !captchaToken}
              style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}
            >
              {loading ? 'Creating account…' : 'Create account →'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 500 }}>Sign in</Link>
        </p>
      </div>
    </div>
  )
}
